import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CustomerLink from "@/components/CustomerLink";
import { Paperclip, ChatText, ClockCounterClockwise, UploadSimple, DownloadSimple, Trash, Eye, X as XIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const STATUSES = ["Open", "In Progress", "Waiting", "Completed", "Cancelled"];
const PRIORITIES = ["Low", "Normal", "High", "Urgent"];
const TYPES = [
  "Accounts Receivable","Accounting","Dispatch","Fleet",
  "Service","Safety","Training","Administration","General",
];

export default function TaskDetailDialog({ task, onClose, onChanged }) {
  const { user } = useAuth();
  const [t, setT] = useState(task);
  const [events, setEvents] = useState([]);
  const [comment, setComment] = useState("");
  const [tab, setTab] = useState("details");
  const fileRef = useRef(null);

  const canReassign = user?.role === "Administrator" || user?.role === "Owner";
  const canDelete = user?.role === "Administrator";
  const canEdit = ["Administrator", "Owner", "Office Staff"].includes(user?.role) || t.assigned_to === user?.email;

  useEffect(() => { setT(task); }, [task]);

  useEffect(() => {
    if (!t?.id) return;
    api.get(`/tasks/${t.id}/events`).then((r) => setEvents(r.data)).catch(() => {});
  }, [t?.id]);

  const patch = async (changes) => {
    try {
      const res = await api.patch(`/tasks/${t.id}`, changes);
      setT(res.data);
      onChanged?.();
      api.get(`/tasks/${t.id}/events`).then((r) => setEvents(r.data));
      toast.success("Task updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const submitComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/tasks/${t.id}/comments`, { body: comment.trim() });
      setComment("");
      const r = await api.get(`/tasks/${t.id}`);
      setT(r.data);
      const e = await api.get(`/tasks/${t.id}/events`);
      setEvents(e.data);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add comment");
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      // Do NOT set Content-Type manually — the browser must generate the
      // multipart/form-data boundary. Explicitly override the axios client
      // default (`application/json`) with `undefined` so axios inspects the
      // FormData and produces the correct header. Setting the string
      // "multipart/form-data" without a boundary corrupts every upload.
      await api.post(`/tasks/${t.id}/attachments`, fd, { headers: { "Content-Type": undefined } });
      const r = await api.get(`/tasks/${t.id}`);
      setT(r.data);
      const e = await api.get(`/tasks/${t.id}/events`);
      setEvents(e.data);
      onChanged?.();
      toast.success("Attachment uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeTask = async () => {
    if (!window.confirm(`Delete task ${t.task_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/tasks/${t.id}`);
      toast.success("Task deleted");
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl" data-testid="task-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="font-mono text-sm text-gray-500">{t.task_number}</span>
            <span>{t.subject}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="mb-3 flex gap-1 border-b border-gray-200">
          {[
            { k: "details", label: "Details" },
            { k: "comments", label: `Comments (${t.comments?.length || 0})`, icon: ChatText },
            { k: "attachments", label: `Attachments (${t.attachments?.length || 0})`, icon: Paperclip },
            { k: "history", label: "History", icon: ClockCounterClockwise },
          ].map((s) => (
            <button
              key={s.k}
              data-testid={`task-tab-${s.k}`}
              onClick={() => setTab(s.k)}
              className={`inline-flex items-center gap-1 border-b-2 px-3 py-1.5 text-xs font-medium ${
                tab === s.k ? "border-[#EB1D25] text-gray-900" : "border-transparent text-gray-500"
              }`}
            >
              {s.icon && <s.icon size={12} />}
              {s.label}
            </button>
          ))}
        </div>

        {tab === "details" && (
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Field label="Type">
              {canEdit ? (
                <select value={t.task_type} onChange={(e) => patch({ task_type: e.target.value })} className="w-full rounded border border-gray-300 px-2 py-1">
                  {TYPES.map((x) => <option key={x}>{x}</option>)}
                </select>
              ) : (<span className="text-gray-800">{t.task_type}</span>)}
            </Field>
            <Field label="Status">
              {canEdit ? (
                <select data-testid="task-status-select" value={t.status} onChange={(e) => patch({ status: e.target.value })} className="w-full rounded border border-gray-300 px-2 py-1">
                  {STATUSES.map((x) => <option key={x}>{x}</option>)}
                </select>
              ) : (<span className="text-gray-800">{t.status}</span>)}
            </Field>
            <Field label="Priority">
              {canEdit ? (
                <select value={t.priority} onChange={(e) => patch({ priority: e.target.value })} className="w-full rounded border border-gray-300 px-2 py-1">
                  {PRIORITIES.map((x) => <option key={x}>{x}</option>)}
                </select>
              ) : (<span className="text-gray-800">{t.priority}</span>)}
            </Field>
            <Field label="Due Date">
              {canEdit ? (
                <input type="date" value={t.due_date || ""} onChange={(e) => patch({ due_date: e.target.value || null })} className="w-full rounded border border-gray-300 px-2 py-1" />
              ) : (<span className="text-gray-800">{t.due_date || "—"}</span>)}
            </Field>
            <Field label="Assigned To">
              {canReassign ? (
                <ReassignBox current={t.assigned_to} onChange={(email) => patch({ assigned_to: email })} />
              ) : (
                <span className="text-gray-800">{t.assigned_to_name}</span>
              )}
            </Field>
            <Field label="Created By"><span className="text-gray-800">{t.created_by_name}</span></Field>
            {t.customer_name && (
              <Field label="Customer">
                <CustomerLink
                  accountNumber={t.customer_account}
                  customerName={t.customer_name}
                  testId={`taskdetail-customer-link-${t.task_number}`}
                />
              </Field>
            )}
            {/* DEC-052 · round-trip origin badge · shown when this task was
                spawned from an in-app operational artifact so the user can
                answer "has someone already acted on this?" quickly. */}
            {t.source_type && ["phone_message", "morning_intelligence"].includes(t.source_type) ? (
              <Field label="Created From">
                <TaskOriginLink task={t} />
              </Field>
            ) : null}
            {t.completion_date && <Field label="Completed"><span className="text-gray-800">{formatDT(t.completion_date)}</span></Field>}
            <div className="col-span-2">
              <Field label="Description">
                {canEdit ? (
                  <textarea
                    data-testid="task-description"
                    defaultValue={t.description}
                    onBlur={(e) => e.target.value !== t.description && patch({ description: e.target.value })}
                    className="min-h-[100px] w-full rounded border border-gray-300 px-2 py-1"
                  />
                ) : (<div className="whitespace-pre-wrap text-gray-800">{t.description || "—"}</div>)}
              </Field>
            </div>
            {canDelete && (
              <div className="col-span-2 border-t border-gray-200 pt-3">
                <button data-testid="task-delete" onClick={removeTask} className="inline-flex items-center gap-1 rounded border border-red-300 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50">
                  <Trash size={12} /> Delete task
                </button>
              </div>
            )}
          </div>
        )}

        {tab === "comments" && (
          <div className="space-y-3">
            {(t.comments || []).length === 0 ? (
              <div className="text-sm text-gray-500">No comments yet.</div>
            ) : (
              t.comments.map((c) => (
                <div key={c.id} data-testid={`task-comment-${c.id}`} className="rounded-md border border-gray-200 bg-[#f9fafb] p-3 text-sm">
                  <div className="mb-1 flex items-baseline justify-between">
                    <div className="font-medium text-gray-900">{c.author_name}</div>
                    <div className="font-mono text-xs text-gray-500">{formatDT(c.created_at)}</div>
                  </div>
                  <div className="whitespace-pre-wrap text-gray-800">{c.body}</div>
                </div>
              ))
            )}
            <div className="border-t border-gray-200 pt-3">
              <textarea
                data-testid="task-new-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a comment…"
                className="min-h-[70px] w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
              <div className="mt-2 text-right">
                <button
                  data-testid="task-submit-comment"
                  onClick={submitComment}
                  disabled={!comment.trim()}
                  className="rounded-md bg-[#EB1D25] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#AF101B] disabled:opacity-40"
                >
                  Add Comment
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === "attachments" && (
          <AttachmentsPanel
            task={t}
            fileRef={fileRef}
            onUpload={uploadFile}
          />
        )}

        {tab === "history" && (
          <ul className="space-y-1.5 text-sm">
            {events.length === 0 ? (
              <li className="text-gray-500">No history yet.</li>
            ) : (
              events.map((e) => (
                <li key={e.id} data-testid={`task-event-${e.id}`} className="border-l-2 border-gray-200 pl-3">
                  <div className="text-gray-800">{e.description}</div>
                  <div className="font-mono text-xs text-gray-500">
                    {e.actor_name} · {formatDT(e.timestamp)}
                  </div>
                </li>
              ))
            )}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</div>
      {children}
    </div>
  );
}

function ReassignBox({ current, onChange }) {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get(`/users`).then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);
  return (
    <select
      value={current}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
    >
      {users.map((u) => (
        <option key={u.email} value={u.email}>{u.full_name} · {u.role}</option>
      ))}
    </select>
  );
}

function formatDT(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}


// ---------------------------------------------------------------------------
// AttachmentsPanel — thumbnails, inline preview (image + PDF), download.
// Uses axios (with auth header) → object URLs so dev-mode header auth still
// works. Previous plain <a href> download links 401'd inside dev-mode because
// browser navigation strips the X-User-Email header.
// ---------------------------------------------------------------------------

function isPreviewable(a) {
  const m = (a.mime_type || "").toLowerCase();
  return m.startsWith("image/") || m === "application/pdf";
}

function AttachmentsPanel({ task, fileRef, onUpload }) {
  const [thumbs, setThumbs] = useState({}); // { [attachmentId]: objectUrl }
  const [previewing, setPreviewing] = useState(null); // { attachment, url }
  const attachments = task.attachments || [];

  // Pre-fetch image thumbnails so the list gives a visual cue at a glance.
  // PDFs are not thumbnailed here — the Preview button loads them on demand.
  useEffect(() => {
    let cancelled = false;
    const created = [];
    (async () => {
      for (const a of attachments) {
        if (!(a.mime_type || "").toLowerCase().startsWith("image/")) continue;
        if (thumbs[a.id]) continue;
        try {
          const r = await api.get(`/tasks/${task.id}/attachments/${a.id}/download`, {
            responseType: "blob",
          });
          if (cancelled) return;
          const url = URL.createObjectURL(r.data);
          created.push(url);
          setThumbs((prev) => ({ ...prev, [a.id]: url }));
        } catch {
          /* silent — falls back to icon */
        }
      }
    })();
    return () => {
      cancelled = true;
      // Object URLs are revoked when the dialog unmounts via the outer
      // effect below; the running loop's URLs are captured here for
      // safety on re-mount races.
      for (const u of created) URL.revokeObjectURL(u);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments.map((a) => a.id).join(",")]);

  // Cleanup all thumb object URLs when the panel unmounts.
  useEffect(() => {
    return () => {
      for (const u of Object.values(thumbs)) URL.revokeObjectURL(u);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPreview = async (a) => {
    try {
      const r = await api.get(`/tasks/${task.id}/attachments/${a.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      setPreviewing({ attachment: a, url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const downloadFile = async (a) => {
    try {
      const r = await api.get(`/tasks/${task.id}/attachments/${a.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Delay revocation so Safari has time to start the download.
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  return (
    <div className="space-y-3" data-testid="task-attachments-panel">
      <input
        ref={fileRef}
        type="file"
        className="hidden"
        onChange={(e) => onUpload(e.target.files?.[0])}
        data-testid="task-file-input"
      />
      <button
        data-testid="task-upload-button"
        onClick={() => fileRef.current?.click()}
        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
      >
        <UploadSimple size={14} weight="bold" /> Upload file
      </button>
      {attachments.length === 0 ? (
        <div className="text-sm text-gray-500">No attachments yet.</div>
      ) : (
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {attachments.map((a) => (
            <AttachmentCard
              key={a.id}
              attachment={a}
              thumbUrl={thumbs[a.id]}
              onPreview={() => openPreview(a)}
              onDownload={() => downloadFile(a)}
            />
          ))}
        </ul>
      )}
      {previewing && (
        <AttachmentPreviewModal
          attachment={previewing.attachment}
          url={previewing.url}
          onClose={() => {
            URL.revokeObjectURL(previewing.url);
            setPreviewing(null);
          }}
        />
      )}
    </div>
  );
}

function AttachmentCard({ attachment: a, thumbUrl, onPreview, onDownload }) {
  const previewable = isPreviewable(a);
  return (
    <li
      data-testid={`task-attachment-card-${a.id}`}
      className="flex gap-3 rounded-md border border-gray-200 bg-white p-2"
    >
      <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded border border-gray-200 bg-gray-50">
        {thumbUrl ? (
          <img
            src={thumbUrl}
            alt=""
            className="h-full w-full object-cover"
            data-testid={`task-attachment-thumb-${a.id}`}
          />
        ) : (a.mime_type || "").toLowerCase() === "application/pdf" ? (
          <span className="text-[10px] font-bold text-red-600">PDF</span>
        ) : (
          <Paperclip size={20} className="text-gray-400" />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-between">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-gray-900" title={a.filename}>
            {a.filename}
          </div>
          <div className="mt-0.5 truncate font-mono text-[11px] text-gray-500">
            {formatBytes(a.size_bytes)} · {a.uploaded_by_name} · {formatDT(a.uploaded_at)}
          </div>
        </div>
        <div className="mt-1.5 flex items-center gap-2">
          {previewable && (
            <button
              type="button"
              data-testid={`task-attachment-preview-${a.id}`}
              onClick={onPreview}
              className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
            >
              <Eye size={11} weight="bold" /> Preview
            </button>
          )}
          <button
            type="button"
            data-testid={`task-attachment-${a.id}`}
            onClick={onDownload}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-700 hover:underline"
          >
            <DownloadSimple size={11} weight="bold" /> Download
          </button>
        </div>
      </div>
    </li>
  );
}

function AttachmentPreviewModal({ attachment: a, url, onClose }) {
  const isImg = (a.mime_type || "").toLowerCase().startsWith("image/");
  return (
    <div
      data-testid={`task-attachment-preview-modal-${a.id}`}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-md bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-gray-900" title={a.filename}>
              {a.filename}
            </div>
            <div className="text-[11px] text-gray-500">
              {a.mime_type} · {formatBytes(a.size_bytes)}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="task-attachment-preview-close"
            className="rounded p-1 text-gray-500 hover:bg-gray-100"
            aria-label="Close preview"
          >
            <XIcon size={16} weight="bold" />
          </button>
        </div>
        <div className="flex-1 overflow-auto bg-gray-100">
          {isImg ? (
            <img
              src={url}
              alt={a.filename}
              className="mx-auto max-h-[80vh] object-contain"
              data-testid="task-attachment-preview-image"
            />
          ) : (
            <iframe
              src={url}
              title={a.filename}
              className="h-[80vh] w-full border-0"
              data-testid="task-attachment-preview-frame"
            />
          )}
        </div>
      </div>
    </div>
  );
}


// ------------------------------------------------------------------
// DEC-052 · round-trip origin link · resolves to the originating
// operational artifact so managers can answer "who acted on this?".
// ------------------------------------------------------------------
function TaskOriginLink({ task }) {
  const t = task || {};
  const ref = t.source_ref || {};
  if (t.source_type === "phone_message") {
    const href = `/messages${ref.customer_id ? `?customer_id=${encodeURIComponent(ref.customer_id)}` : ""}`;
    return (
      <a href={href} data-testid={`taskdetail-origin-${t.task_number}`}
         className="inline-flex items-center gap-1 text-blue-700 hover:underline">
        Phone Message
        {ref.caller_name ? <> · {ref.caller_name}</> : null}
      </a>
    );
  }
  if (t.source_type === "morning_intelligence") {
    const label = ({
      customer_intel: "Customer Intelligence",
      supplier_intel: "Supplier Intelligence",
      fleet:          "Fleet",
      market:         "Market",
      operations:     "Operations",
    })[ref.section] || "Morning Brief";
    return (
      <a href="/brief" data-testid={`taskdetail-origin-${t.task_number}`}
         className="inline-flex items-center gap-1 text-blue-700 hover:underline">
        Morning Brief · {label}
        {ref.supplier ? <> · {ref.supplier}</> : null}
        {ref.customer_name ? <> · {ref.customer_name}</> : null}
      </a>
    );
  }
  return <span className="text-gray-600">{t.source_type}</span>;
}
